import type { PendingAttachment } from './types'

// Utility functions for the Inbox feature

export function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

export function isUserOnline(lastSeenAt: string | null): boolean {
  if (!lastSeenAt) return false
  const lastSeen = new Date(lastSeenAt)
  const threeMinutesAgo = new Date(Date.now() - 3 * 60 * 1000)
  return lastSeen > threeMinutesAgo
}

const AVATAR_COLORS = [
  'bg-blue-500',
  'bg-emerald-500',
  'bg-violet-500',
  'bg-amber-500',
  'bg-rose-500',
  'bg-cyan-500',
  'bg-indigo-500',
  'bg-pink-500',
]

export function getAvatarColor(name: string): string {
  const index = name.charCodeAt(0) % AVATAR_COLORS.length
  return AVATAR_COLORS[index]
}

export function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000)

  if (diffInSeconds < 60) return 'agora'
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m`
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h`
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d`
  return date.toLocaleDateString('pt-BR', { month: 'short', day: 'numeric' })
}

// Full date string for tooltip display (e.g. "5 de fevereiro de 2026, 14:30")
export function formatFullDate(dateString: string): string {
  return new Date(dateString).toLocaleString('pt-BR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function formatDateSeparator(dateString: string): string {
  const date = new Date(dateString)
  const today = new Date()
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)

  if (date.toDateString() === today.toDateString()) {
    return 'Hoje'
  }
  if (date.toDateString() === yesterday.toDateString()) {
    return 'Ontem'
  }
  return date.toLocaleDateString('pt-BR', { day: 'numeric', month: 'long' })
}

export function shouldShowDateSeparator(
  currentDate: string,
  previousDate: string | null,
  isFirstItem: boolean,
  hasMultipleDays: boolean
): boolean {
  if (isFirstItem) return hasMultipleDays
  if (!previousDate) return false
  const current = new Date(currentDate)
  const previous = new Date(previousDate)
  return current.toDateString() !== previous.toDateString()
}

export function timelineHasMultipleDays(items: { created_at: string }[]): boolean {
  if (items.length < 2) return false
  const firstDate = new Date(items[0].created_at).toDateString()
  return items.some(item => new Date(item.created_at).toDateString() !== firstDate)
}

// Convert relative URLs to absolute URLs pointing to Codental main app
const CODENTAL_BASE_URL = 'https://www.codental.com.br'

export function toAbsoluteUrl(url: string): string {
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url
  }
  return `${CODENTAL_BASE_URL}${url.startsWith('/') ? '' : '/'}${url}`
}

export function formatRecordingTime(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m}:${s.toString().padStart(2, '0')}`
}

export function getCsrfToken(): string {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
}

// Convert File objects into PendingAttachment objects with preview URLs for images
export function filesToPendingAttachments(files: File[]): PendingAttachment[] {
  return files.map((file) => ({
    id: Math.random().toString(36).substr(2, 9),
    file,
    previewUrl:
      file.type.startsWith('image/') && file.type !== 'image/heic' && file.type !== 'image/heif'
        ? URL.createObjectURL(file)
        : null,
  }))
}

// Validate WhatsApp file size limits. Returns an error message or null if valid.
export function validateWhatsAppFileSize(files: File[]): string | null {
  const maxSizes: Record<string, number> = {
    image: 5 * 1024 * 1024,
    video: 16 * 1024 * 1024,
    audio: 16 * 1024 * 1024,
  }
  const defaultMax = 100 * 1024 * 1024

  for (const file of files) {
    const type = file.type.split('/')[0]
    const limit = maxSizes[type] || defaultMax
    if (file.size > limit) {
      const limitMB = Math.floor(limit / (1024 * 1024))
      return `Arquivo excede o limite do WhatsApp (${limitMB}MB para ${type})`
    }
  }

  return null
}

// Convenience wrapper for JSON API requests with CSRF token
export function fetchJson(url: string, options: { method: string; body?: unknown }): Promise<Response> {
  return fetch(url, {
    method: options.method,
    headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrfToken() },
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
  })
}

// Convenience wrapper for FormData API requests with CSRF token
export function fetchFormData(url: string, body: FormData): Promise<Response> {
  return fetch(url, {
    method: 'POST',
    headers: { 'X-CSRF-Token': getCsrfToken() },
    body,
  })
}
